import { supportedLocales, localeDirection } from '@/config/locales'
import { ErpDropdown, ErpDropdownItem, ErpDropdownDivider, ErpHeader, ErpButton, ErpDiv, ErpSpan, ErpI } from '@/components/erp';

import { Permission } from '@/enums/Permission';
import React, { useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  toggle,
  toggleMobile,
} from '@/redux/slices/sidebarSlice'
import { setLanguage as setLanguageAction } from '@/redux/slices/languageSlice'
import { setRtl } from '@/redux/slices/configSlice'
import { logout as logoutThunk, checkAdminSession } from '@/redux/slices/authSlice'
import { setLocale } from '@/i18n'
import { useCommandPalette } from '@/hooks/useCommandPalette'
import { useDensity } from '@/hooks/useDensity'
import { registerEchoListener, onRealtimeResync } from '@/services/echoClient'
import { getEcho } from '@/hooks/useEcho'
import { getTenantId } from '@/services/tokenStorage'


import { ThemeSwitcher } from '@/components/ThemeSwitcher'
import { ModeToggle } from '@/components/ModeToggle'
import { Apple, Bell, CheckCheck, Globe, Menu, Power, Search, Settings, User, UserCog , Maximize2, Minimize2 } from 'lucide-react';
import { useGetPendingApprovalsQuery } from '@/redux/api/payrollApi';
import {
  useGetApprovalNotificationsQuery,
  useMarkApprovalNotificationReadMutation,
  useMarkAllApprovalNotificationsReadMutation,
} from '@/redux/api/approvalsApi';
import type { ApprovalNotification } from '@/redux/api/approvalsApi';

interface FeedItem {
  id: string
  titleKey: string
  metaText: string
  icon: string
  colorClass: string
  action: () => void
}

export const TopNav: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const permissions = useAppSelector((state) => state.auth.Permissions)
  const notifs = useAppSelector((state) => state.auth.notifs)
  const isSuperAdmin = useAppSelector((state) => state.auth.isSuperAdmin)
  const user = useAppSelector((state) => state.auth.user) as Record<string, unknown>

  const cp = useCommandPalette()
  const density = useDensity()

  const totalNotifs = notifs ? notifs.quantityAlerts + notifs.batchAlerts : 0

  // F-2b — pending approvals badge (payroll stages awaiting the current user).
  const canViewPayroll = !!permissions?.includes(Permission.PAYROLL_RUN_APPROVE)
  const { data: approvalsData } = useGetPendingApprovalsQuery(undefined, {
    skip: !canViewPayroll,
    pollingInterval: 120_000,
  })
  // The api interceptor deep-camelizes response keys, so read `pendingApprovals`
  // (fall back to the raw snake key for safety).
  const pendingPayload = approvalsData?.data?.pendingApprovals ?? approvalsData?.data?.pending_approvals
  const pendingApprovals = Array.isArray(pendingPayload)
    ? pendingPayload.length
    : 0

  // F-06 — approval notifications inbox (polled and refreshed by private
  // inbox events).
  const {
    data: approvalNotifsData,
    refetch: refetchApprovalNotifications,
  } = useGetApprovalNotificationsQuery(undefined, {
    pollingInterval: 120_000,
    refetchOnFocus: true,
  })
  const [markApprovalNotificationRead] = useMarkApprovalNotificationReadMutation()
  const [markAllApprovalNotificationsRead] = useMarkAllApprovalNotificationsReadMutation()

  const approvalNotifications: ApprovalNotification[] = useMemo(
    () => approvalNotifsData?.data?.notifications ?? [],
    [approvalNotifsData]
  )
  const approvalUnreadCount = approvalNotifsData?.data?.unreadCount ?? 0

  const currentTenantId = useAppSelector((state) => state.auth.tenantId ?? state.tenant?.tenantId) ?? getTenantId()

  // Realtime approval and operational notifications arrive only on this
  // user's private inbox channel, so tenant-wide subscribers cannot see
  // another approver's workflow metadata. Registered through the echoClient
  // registry so an Echo rebuild replays listeners onto the new instance.
  // Tenant id is re-evaluated
  // on tenant switch so a switch cleans up the previous tenant channel before
  // subscribing to the new one. Retries every 3s until the tenant id is available.
  useEffect(() => {
    let cleanup: (() => void) | undefined
    let subscribedTenantId: string | number | null = null
    let timer: ReturnType<typeof setInterval> | undefined

    const stopRetry = () => {
      if (timer) {
        clearInterval(timer)
        timer = undefined
      }
    }

    const trySubscribe = () => {
      const tenantId = currentTenantId ?? getTenantId();
      // Registry replays onto a fresh Echo, but an explicit live-instance
      // check avoids a registration that only queues while Echo is gone.
      if (!tenantId || !getEcho()) return false

      if (cleanup) {
        if (String(subscribedTenantId) === String(tenantId)) {
          // Listener for this channel is already live — a second
          // registration would double-fire the refetch and orphan the
          // first unregister handle. The retry poll must stop too.
          stopRetry()
          return true
        }
        // Tenant switched without a remount (storage changed before the
        // redux id): release the previous tenant's channel first —
        // previously it stayed subscribed until unmount.
        cleanup()
        cleanup = undefined
      }

      const inboxChannel = user?.id ? `tenant.${tenantId}.inbox.${user.id}` : null
      const refreshInbox = () => {
        if (String(getTenantId() ?? '') === String(tenantId)) refetchApprovalNotifications()
      }
      const stopApproval = inboxChannel
        ? registerEchoListener(inboxChannel, '.ApprovalActionPending', refreshInbox)
        : () => {}
      const stopInbox = inboxChannel
        ? registerEchoListener(inboxChannel, '.InboxUpdated', refreshInbox)
        : () => {}
      cleanup = () => { stopApproval(); stopInbox() }
      subscribedTenantId = tenantId
      stopRetry()
      return true
    }

    if (!trySubscribe()) {
      timer = setInterval(() => {
        trySubscribe()
      }, 3000)
    }
    // Echo rebuilds (login/tenant switch) land after mount — re-attempt via
    // the shared resync signal instead of relying on the poll above alone.
    // trySubscribe is idempotent, so a rebuild never duplicates the listener.
    const unbindResync = onRealtimeResync(() => {
      trySubscribe()
    })

    return () => {
      stopRetry()
      unbindResync()
      cleanup?.()
    }
  }, [refetchApprovalNotifications, currentTenantId, user?.id])

  const onApprovalNotificationClick = useCallback((notification: ApprovalNotification) => {
    if (!notification.readAt) {
      markApprovalNotificationRead(notification.id)
    }
    const deepLink = notification.data?.deepLink
    if (deepLink) navigate(deepLink)
  }, [markApprovalNotificationRead, navigate])

  const onMarkAllApprovalNotificationsRead = useCallback(() => {
    if (approvalUnreadCount > 0) {
      markAllApprovalNotificationsRead()
    }
  }, [markAllApprovalNotificationsRead, approvalUnreadCount])

  const goReports = useCallback((tab: number) => {
    navigate(`/app/reports?tab=${tab}`)
  }, [navigate])

  const feedItems = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = []
    if (pendingApprovals > 0) {
      items.push({
        id: 'approvals',
        titleKey: 'My_Approvals',
        metaText: `${pendingApprovals} ${t('Pending')}`,
        icon: 'user-check',
        colorClass: 'text-indigo-600',
        action: () => navigate('/app/hrm/my-approvals'),
      })
    }
    if (notifs && notifs.quantityAlerts > 0) {
      items.push({
        id: 'qty',
        titleKey: 'ProductQuantityAlerts',
        metaText: `${notifs.quantityAlerts} ${t('Alerts')}`,
        icon: 'triangle-alert',
        colorClass: 'text-warning',
        action: () => goReports(21),
      })
    }
    if (notifs && notifs.batchAlerts > 0) {
      items.push({
        id: 'batch',
        titleKey: 'BatchExpiryAlerts',
        metaText: `${notifs.batchAlerts} ${t('Alerts')}`,
        icon: 'clock',
        colorClass: 'text-destructive',
        action: () => goReports(7),
      })
    }
    return items
  }, [notifs, pendingApprovals, t, goReports, navigate])

  const initials = useMemo(() => {
    const first = String(user?.firstname ?? '').trim()
    const last = String(user?.lastname ?? '').trim()
    if (first || last) return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase()
    const uname = String(user?.username ?? '').trim()
    return uname ? uname[0].toUpperCase() : 'U'
  }, [user])

  async function changeLanguage(l: string) {
    // setLocale loads the lazy Arabic chunk: without a guard a rejected import
    // left an unhandled rejection and the language silently never switched.
    try {
      const ok = await setLocale(l)
      if (ok) {
        dispatch(setLanguageAction(l))
        const isRtl = localeDirection(l) === 'rtl'
        dispatch(setRtl(isRtl))
        document.documentElement.dir = isRtl ? 'rtl' : 'ltr'
        document.documentElement.lang = l
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[top-nav] failed to switch locale:', error)
    }
  }

  function onLogout() {
    dispatch(logoutThunk() as any)
  }
  function returnToAdmin() {
    navigate('/admin/dashboard')
  }
  function goProfile() {
    navigate('/app/profile')
  }
  function goSettings() {
    navigate('/app/settings/System_settings')
  }

  function onMenuToggle() {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      dispatch(toggleMobile())
    } else {
      dispatch(toggle())
    }
  }
  function openCommandPalette() {
    cp.open()
  }

  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(checkAdminSession() as any)
    }
  }, [isSuperAdmin, dispatch])

  return (
    <ErpHeader className="flex h-16 items-center px-2 md:px-6 bg-card border-b border-border shadow-sm sticky top-0 z-40 gap-2 md:gap-4 w-full">
      <ErpButton
        type="button"
        className="lg:hidden text-muted-foreground hover:text-foreground transition-colors p-2 -ms-2 rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
        title={t('Toggle menu')}
        aria-label={t('Toggle menu')}
        onClick={onMenuToggle}
      >
        <Menu size={20} className="text-lg"  />
      </ErpButton>
      <ErpButton
        type="button"
        className="flex items-center gap-2 px-3 py-1.5 text-base text-muted-foreground bg-muted/50 hover:bg-muted border border-border rounded-md transition-colors me-2 max-w-[1440px] w-full sm:max-w-[1000px] shadow-sm"
        title={t('Search')}
        onClick={openCommandPalette}
      >
        <Search size={20}  />
        <ErpSpan className="truncate flex-1 text-left hidden sm:inline-block">{t('SearchOrJump')}</ErpSpan>
        <ErpSpan className="hidden md:flex items-center gap-1 text-xs font-semibold uppercase tracking-widest px-2 py-1.5 rounded-md border border-border bg-background shadow-sm">
          <Apple size={14}  /> K
        </ErpSpan>
      </ErpButton>
      <ErpDiv className="flex-1" />
      <ErpDropdown right contentClass="w-80 mt-1" trigger={
        <ErpButton variant="ghost" className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring">
            <Bell size={20} className="text-lg"  />
            {approvalUnreadCount > 0 ? (
              <ErpSpan className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center pointer-events-none">
                {approvalUnreadCount > 99 ? '99+' : approvalUnreadCount}
              </ErpSpan>
            ) : (totalNotifs > 0 || pendingApprovals > 0) ? (
              <ErpSpan className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-destructive animate-pulse" />
            ) : null}
        </ErpButton>
      }>


        <ErpDiv className="flex items-center justify-between font-semibold gap-2">
            <ErpSpan className="flex items-center gap-2"><Bell size={16}  /> {t('Notifications')}</ErpSpan>
            {approvalUnreadCount > 0 && (
            <ErpButton
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded px-1.5 py-0.5 transition-colors"
                title={t('Mark_All_Read') || 'Mark all notifications as read'}
                onClick={onMarkAllApprovalNotificationsRead}
              >
                <CheckCheck size={13} /> {t('Mark_All_Read') || 'Mark all read'}
            </ErpButton>
            )}
        </ErpDiv>
        <ErpDropdownDivider />
          {approvalNotifications.slice(0, 5).map((notification) => (
          <ErpDropdownItem
              key={notification.id}
              onClick={() => onApprovalNotificationClick(notification)}
              className="cursor-pointer py-2.5"
            >
              <ErpDiv className={`flex flex-col w-full ${notification.readAt ? 'opacity-60' : ''}`}>
                <ErpSpan className="font-medium text-sm leading-tight flex items-center gap-1.5">
                  {!notification.readAt && (
                    <ErpSpan className="w-1.5 h-1.5 rounded-full bg-indigo-600 shrink-0" aria-hidden />
                  )}
                  {t(notification.data?.title ?? 'Approval_Required')}
                </ErpSpan>
                {notification.data?.body && (
                  <ErpSpan className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.data.body}</ErpSpan>
                )}
                <ErpSpan className="text-[11px] text-muted-foreground mt-0.5">
                  {notification.createdAt ? new Date(notification.createdAt).toLocaleString() : ''}
                </ErpSpan>
              </ErpDiv>
          </ErpDropdownItem>
          ))}
        {approvalNotifications.length > 0 && <ErpDropdownDivider />}
          {feedItems.map((item) => (
          <ErpDropdownItem key={item.id} onClick={item.action} className="cursor-pointer py-3">
              <ErpI className={`${item.icon} ${item.colorClass} text-lg w-6 text-center me-3`} />
              <ErpDiv className="flex flex-col">
                <ErpSpan className="font-medium text-sm leading-tight">{t(item.titleKey)}</ErpSpan>
                <ErpSpan className="text-xs text-muted-foreground mt-0.5">{item.metaText}</ErpSpan>
              </ErpDiv>
          </ErpDropdownItem>
          ))}
          {(totalNotifs === 0 && pendingApprovals === 0 && approvalNotifications.length === 0) && (
          <ErpDropdownItem disabled className="text-center py-4 text-muted-foreground">
              {t('NoNotifications')}
          </ErpDropdownItem>
          )}

      </ErpDropdown>
      <ThemeSwitcher />
      <ModeToggle />
      <ErpDropdown right contentClass="mt-1" trigger={
        <ErpButton variant="ghost" className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus:outline-none focus:ring-2 focus:ring-ring">
            <Globe size={20} className="text-lg"  />
        </ErpButton>
      }>


        {supportedLocales.map(language => (
          <ErpDropdownItem key={language.code} onClick={() => changeLanguage(language.code)} className="cursor-pointer">
            {t(language.nameKey)}
          </ErpDropdownItem>
        ))}

      </ErpDropdown>
      <ErpDropdown right contentClass="w-56 mt-1" trigger={
        <ErpButton variant="ghost" className="rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background ms-1">
          <ErpSpan className="inline-flex h-8 w-8 shrink-0 overflow-hidden rounded-full border border-border">
            <ErpSpan className="flex size-full items-center justify-center bg-primary/10 text-primary font-semibold text-sm">
                {initials}
            </ErpSpan>
          </ErpSpan>
        </ErpButton>
      }>


        <ErpDropdownItem onClick={goProfile} className="cursor-pointer">
            <User size={16} className="me-2 w-4 text-center opacity-70"  /> {t('profil')}
        </ErpDropdownItem>
          {permissions && permissions.includes(Permission.SETTING_SYSTEM) && (
          <ErpDropdownItem onClick={goSettings} className="cursor-pointer">
              <Settings size={16} className="me-2 w-4 text-center opacity-70"  /> {t('Settings')}
          </ErpDropdownItem>
          )}
        <ErpDropdownItem onClick={() => density.toggle()} className="cursor-pointer">
            {density.value === 'compact' ? <Maximize2 size={16} className="me-2 opacity-70" /> : <Minimize2 size={16} className="me-2 opacity-70" />}
            {density.value === 'compact' ? (t('Comfortable density') || 'Comfortable density') : (t('Compact density') || 'Compact density')}
        </ErpDropdownItem>
          {isSuperAdmin && (
          <ErpDropdownItem onClick={returnToAdmin} className="cursor-pointer">
              <UserCog size={16} className="me-2 w-4 text-center opacity-70"  /> {t('ReturnToAdmin')}
          </ErpDropdownItem>
          )}
        <ErpDropdownDivider />
        <ErpDropdownItem onClick={onLogout} className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10">
            <Power size={16} className="me-2 w-4 text-center opacity-70"  /> {t('logout')}
        </ErpDropdownItem>

      </ErpDropdown>
    </ErpHeader>
  );
}

export default TopNav
